import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Eye, Phone, MessageCircle } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { CallLogModal } from "../components/common/CallLogModal";
import { CallHistoryModal } from "../components/common/CallHistoryModal";
import { CustomerTagBadge } from "../components/common/CustomerTagBadge";
import { HistoryButton } from "../components/common/CustomerHistoryModal";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate } from "../lib/utils";
import { toast } from "sonner";

export const CustomersPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [branches, setBranches] = useState([]);
  const [cres, setCres] = useState([]);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [callLogContact, setCallLogContact] = useState(null);
  const [historyContact, setHistoryContact] = useState(null);
  
  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    email: "",
    branch_id: "",
    assigned_cre_id: "",
    notes: ""
  });

  useEffect(() => {
    fetchCustomers();
    if (isAdmin()) {
      fetchBranches();
      fetchCREs();
    }
  }, [statusFilter]);

  const fetchCustomers = async () => {
    try {
      let url = "/customers";
      const params = [];
      if (statusFilter && statusFilter !== "all") {
        params.push(`status=${statusFilter}`);
      }
      // For CRE users, use cre_user_id to filter by their assigned branches
      if (!isAdmin() && user?.id) {
        params.push(`cre_user_id=${user.id}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      const data = await apiRequest(url);
      setCustomers(data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchCREs = async () => {
    try {
      const data = await apiRequest("/users?role=cre");
      setCres(data);
    } catch (error) {
      console.error("Failed to fetch CREs:", error);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      await apiRequest("/customers", {
        method: "POST",
        body: JSON.stringify(newCustomer)
      });
      toast.success("Customer added successfully");
      setShowAddModal(false);
      setNewCustomer({
        name: "",
        phone: "",
        email: "",
        branch_id: "",
        assigned_cre_id: "",
        notes: ""
      });
      fetchCustomers();
    } catch (error) {
      toast.error(error.message || "Failed to add customer");
    }
  };

  const handleCall = (customer, skipDialer = false) => {
    // First open the phone dialer (unless skipped, e.g., from QuickActions which handles it)
    if (!skipDialer && customer.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
    // Show call log modal after a brief delay to allow dialer to open
    setTimeout(() => {
      setCallLogContact({
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        status: customer.status,
        type: "customer",
        branch_id: customer.branch_id
      });
    }, skipDialer ? 0 : 800);
  };

  const handleViewHistory = (customer) => {
    setHistoryContact({
      id: customer.id,
      name: customer.name,
      type: "customer"
    });
  };

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "due", label: "Due" },
    { value: "overdue", label: "Overdue" },
    { value: "delayed", label: "Delayed" },
    { value: "no_show", label: "No Show" },
    { value: "red_flag", label: "Red Flag" }
  ];

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {isAdmin() ? "All Customers" : "My Customers"}
          </h1>
          <p className="text-muted-foreground">
            Manage your customer relationships
          </p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Customer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddCustomer} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    required
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  data-testid="customer-email-input"
                />
              </div>
              {isAdmin() && (
                <>
                  <div>
                    <Label htmlFor="branch">Branch</Label>
                    <Select
                      value={newCustomer.branch_id}
                      onValueChange={(value) => setNewCustomer({ ...newCustomer, branch_id: value })}
                    >
                      <SelectTrigger data-testid="customer-branch-select">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cre">Assign to CRE</Label>
                    <Select
                      value={newCustomer.assigned_cre_id}
                      onValueChange={(value) => setNewCustomer({ ...newCustomer, assigned_cre_id: value })}
                    >
                      <SelectTrigger data-testid="customer-cre-select">
                        <SelectValue placeholder="Select CRE" />
                      </SelectTrigger>
                      <SelectContent>
                        {cres.map((cre) => (
                          <SelectItem key={cre.id} value={cre.id}>
                            {cre.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newCustomer.notes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                  rows={3}
                  data-testid="customer-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-customer-button">
                  Save Customer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bento-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="customer-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="customer-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table - Desktop */}
      <Card className="bento-card hidden md:block">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead>Last Service</TableHead>
                  <TableHead>Last Remark</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No customers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} className="table-high-density cursor-pointer hover:bg-muted/50" data-testid={`customer-row-${customer.id}`} onClick={() => navigate(`/customers/${customer.id}`)}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatPhone(customer.phone)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {customer.vehicle || "-"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={customer.status} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <CustomerTagBadge 
                          customerId={customer.id} 
                          currentTag={customer.tag || "normal"} 
                          size="small"
                          onUpdate={fetchCustomers}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {customer.last_service_date ? formatDate(customer.last_service_date) : "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                        {customer.last_remark || "-"}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <HistoryButton 
                            customerId={customer.id} 
                            customerName={customer.name}
                          />
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="quick-action-btn text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => navigate(`/customers/${customer.id}`)}
                                  data-testid={`view-profile-${customer.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Profile</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <QuickActions
                            phone={customer.phone}
                            onCall={() => handleCall(customer, true)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Customers List - Mobile */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No customers found</div>
        ) : (
          filteredCustomers.map((customer) => (
            <Card 
              key={customer.id} 
              className="bento-card"
              data-testid={`customer-card-${customer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0" onClick={() => navigate(`/customers/${customer.id}`)}>
                    <p className="font-medium truncate">{customer.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{formatPhone(customer.phone)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <StatusBadge status={customer.status} />
                    <CustomerTagBadge 
                      customerId={customer.id} 
                      currentTag={customer.tag || "normal"} 
                      size="small"
                      onUpdate={fetchCustomers}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span>{customer.vehicle || "No vehicle"}</span>
                  <span>Last: {customer.last_service_date ? formatDate(customer.last_service_date) : "-"}</span>
                </div>
                {customer.last_remark && (
                  <p className="text-xs text-muted-foreground truncate mb-3 italic">
                    "{customer.last_remark}"
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleCall(customer)}
                    data-testid={`mobile-call-${customer.id}`}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                  <a
                    href={`https://wa.me/91${customer.phone?.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-green-600 border-green-200 hover:bg-green-50"
                      data-testid={`mobile-whatsapp-${customer.id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WA
                    </Button>
                  </a>
                  <HistoryButton 
                    customerId={customer.id} 
                    customerName={customer.name}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    data-testid={`mobile-view-${customer.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Call Log Modal */}
      <CallLogModal
        open={!!callLogContact}
        onClose={() => setCallLogContact(null)}
        contact={callLogContact}
        onSuccess={fetchCustomers}
      />

      {/* Call History Modal */}
      <CallHistoryModal
        open={!!historyContact}
        onClose={() => setHistoryContact(null)}
        contactId={historyContact?.id}
        contactType={historyContact?.type}
        contactName={historyContact?.name}
      />
    </div>
  );
};

export default CustomersPage;
